import React, { useState, useEffect, useReducer, useCallback } from "react";
import { format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Reducer for blogs
const blogInitialState = {
  blogs: [],
  loading: false,
  error: null,
};

const blogReducer = (state, action) => {
  switch (action.type) {
    case "SET_BLOGS":
      return { ...state, blogs: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

// Input Components
const TextInput = ({ label, name, value, onChange, placeholder, error, type = "text", icon }) => (
  <div className="relative">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {icon}
        </span>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full p-3 ${icon ? "pl-10" : "pl-3"} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
      />
    </div>
    {error && (
      <p id={`${name}-error`} className="mt-1 text-sm text-red-500">
        {error}
      </p>
    )}
  </div>
);

const TextAreaInput = ({ label, name, value, onChange, placeholder, error, icon }) => (
  <div className="relative">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-4 text-gray-400">{icon}</span>
      )}
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full p-3 ${icon ? "pl-10" : "pl-3"} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
      />
    </div>
    {error && (
      <p id={`${name}-error`} className="mt-1 text-sm text-red-500">
        {error}
      </p>
    )}
  </div>
);

const Edit = () => {
  const [blogState, blogDispatch] = useReducer(blogReducer, blogInitialState);
  const { blogs, loading: blogLoading, error: blogError } = blogState;
  const [showBlogEditModal, setShowBlogEditModal] = useState(null);
  const [createErrors, setCreateErrors] = useState({});
  const [blogFormData, setBlogFormData] = useState({
    id: null,
    title: "",
    excerpt: "",
    category: "",
    readTime: "",
    date: "",
  });

  // WebSocket for real-time updates
  useEffect(() => {
    const ws = new WebSocket("ws://localhost:8080");
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === "reloadBlogs") {
        fetchBlogs();
      }
    };
    ws.onclose = () => console.log("WebSocket disconnected");
    return () => ws.close();
  }, []);

  // Fetch blogs
  const fetchBlogs = useCallback(async () => {
    try {
      blogDispatch({ type: "SET_LOADING", payload: true });
      blogDispatch({ type: "SET_ERROR", payload: null });
      const response = await fetch("/api/blogs");
      if (!response.ok) throw new Error("Failed to fetch blogs");
      const data = await response.json();
      blogDispatch({ type: "SET_BLOGS", payload: data });
    } catch (err) {
      blogDispatch({ type: "SET_ERROR", payload: err.message || "Error loading blogs" });
      toast.error("Failed to load blogs");
    } finally {
      blogDispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const validateBlogForm = () => {
    const errors = {};
    if (!blogFormData.title) errors.title = "Title is required";
    if (!blogFormData.excerpt) errors.excerpt = "Excerpt is required";
    if (!blogFormData.category) errors.category = "Category is required";
    if (!blogFormData.readTime) errors.readTime = "Read time is required";
    if (!blogFormData.date) errors.date = "Date is required";
    return errors;
  };

  const handleBlogChange = (e) => {
    const { name, value } = e.target;
    setBlogFormData((prev) => ({ ...prev, [name]: value }));
    setCreateErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateBlog = async (e) => {
    e.preventDefault();
    const errors = validateBlogForm();
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }
    try {
      blogDispatch({ type: "SET_LOADING", payload: true });
      const response = await fetch("/api/blogs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(blogFormData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create blog");
      }
      setBlogFormData({ id: null, title: "", excerpt: "", category: "", readTime: "", date: "" });
      setShowBlogEditModal(null);
      fetchBlogs();
      toast.success("Blog created successfully!");
    } catch (err) {
      toast.error(err.message || "Error creating blog");
    } finally {
      blogDispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleUpdateBlog = async (e) => {
    e.preventDefault();
    const errors = validateBlogForm();
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }
    try {
      blogDispatch({ type: "SET_LOADING", payload: true });
      const response = await fetch(`/api/blogs/${blogFormData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(blogFormData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update blog");
      }
      setShowBlogEditModal(null);
      setBlogFormData({ id: null, title: "", excerpt: "", category: "", readTime: "", date: "" });
      fetchBlogs();
      toast.success("Blog updated successfully!");
    } catch (err) {
      toast.error(err.message || "Error updating blog");
    } finally {
      blogDispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleDeleteBlog = async (id) => {
    try {
      blogDispatch({ type: "SET_LOADING", payload: true });
      const response = await fetch(`/api/blogs/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete blog");
      }
      fetchBlogs();
      toast.success("Blog deleted successfully!");
    } catch (err) {
      toast.error(err.message || "Error deleting blog");
    } finally {
      blogDispatch({ type: "SET_LOADING", payload: false });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-blue-900 mb-6 text-center">
          Blog Management
        </h1>
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Manage Blog Posts</h2>
          <button
            onClick={() => {
              setShowBlogEditModal("create");
              setBlogFormData({ id: null, title: "", excerpt: "", category: "", readTime: "", date: "" });
            }}
            className="mb-6 px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg hover:from-blue-600 hover:to-blue-800 transition-all duration-300 shadow-md"
          >
            Add New Blog
          </button>
          {blogLoading && (
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 mx-auto"></div>
          )}
          {blogError && <p className="text-red-500 text-center">{blogError}</p>}
          {!blogLoading && !blogError && (
            <div className="space-y-4">
              {blogs.map((blog) => (
                <div key={blog.id} className="flex justify-between items-center border-b py-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{blog.title}</h3>
                    <p className="text-gray-600 text-sm">{blog.excerpt}</p>
                    <p className="text-sm text-gray-500">
                      {blog.category} | {blog.readTime} | {format(new Date(blog.date), "MMMM dd, yyyy")}
                    </p>
                  </div>
                  <div className="space-x-3">
                    <button
                      onClick={() => {
                        setShowBlogEditModal(blog.id);
                        setBlogFormData({
                          id: blog.id,
                          title: blog.title,
                          excerpt: blog.excerpt,
                          category: blog.category,
                          readTime: blog.readTime,
                          date: blog.date.split("T")[0], // Format for input
                        });
                      }}
                      className="text-blue-500 hover:text-blue-700 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBlog(blog.id)}
                      className="text-red-500 hover:text-red-700 font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Blog Edit/Create Modal */}
        {showBlogEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full transform transition-all duration-300 scale-100">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                {showBlogEditModal === "create" ? "Create Blog" : "Edit Blog"}
              </h2>
              <form onSubmit={showBlogEditModal === "create" ? handleCreateBlog : handleUpdateBlog} className="space-y-6">
                <TextInput
                  label="Title"
                  name="title"
                  value={blogFormData.title}
                  onChange={handleBlogChange}
                  placeholder="Enter blog title"
                  error={createErrors.title}
                  icon="📝"
                />
                <TextAreaInput
                  label="Excerpt"
                  name="excerpt"
                  value={blogFormData.excerpt}
                  onChange={handleBlogChange}
                  placeholder="Enter blog excerpt"
                  error={createErrors.excerpt}
                  icon="📖"
                />
                <TextInput
                  label="Category"
                  name="category"
                  value={blogFormData.category}
                  onChange={handleBlogChange}
                  placeholder="Enter category"
                  error={createErrors.category}
                  icon="🏷️"
                />
                <TextInput
                  label="Read Time"
                  name="readTime"
                  value={blogFormData.readTime}
                  onChange={handleBlogChange}
                  placeholder="e.g., 5 min read"
                  error={createErrors.readTime}
                  icon="⏳"
                />
                <TextInput
                  label="Date"
                  name="date"
                  type="date"
                  value={blogFormData.date}
                  onChange={handleBlogChange}
                  placeholder="Select date"
                  error={createErrors.date}
                  icon="📅"
                />
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBlogEditModal(null);
                      setBlogFormData({ id: null, title: "", excerpt: "", category: "", readTime: "", date: "" });
                      setCreateErrors({});
                    }}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
                    aria-label="Cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg font-medium transition-all duration-300 ${
                      blogLoading ? "opacity-50 cursor-not-allowed" : "hover:from-blue-600 hover:to-blue-800"
                    }`}
                    disabled={blogLoading}
                    aria-label={showBlogEditModal === "create" ? "Create blog" : "Update blog"}
                  >
                    {blogLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                    ) : showBlogEditModal === "create" ? "Create Blog" : "Update Blog"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Toast Notifications */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </div>
  );
};

export default Edit;
